import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

export type Language = 'en' | 'ar';

export interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: Record<Language, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    direction: 'ltr',
  },
  ar: {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    direction: 'rtl',
  },
};

/**
 * Custom hook for managing language preferences
 * Provides utilities for switching languages and accessing language metadata
 */
export function useLanguage() {
  const { i18n } = useTranslation();

  const currentLanguage = (i18n.language || 'en') as Language;
  const currentConfig = SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES.en;

  /**
   * Switch to a different language
   * Updates i18n instance and persists to localStorage
   */
  const changeLanguage = useCallback(
    async (language: Language) => {
      try {
        await i18n.changeLanguage(language);
        // i18next-browser-languagedetector automatically saves to localStorage
        return true;
      } catch (error) {
        console.error('Failed to change language:', error);
        return false;
      }
    },
    [i18n]
  );

  /**
   * Toggle between English and Arabic
   */
  const toggleLanguage = useCallback(async () => {
    const newLanguage: Language = currentLanguage === 'en' ? 'ar' : 'en';
    return changeLanguage(newLanguage);
  }, [currentLanguage, changeLanguage]);

  /**
   * Check if a specific language is currently active
   */
  const isLanguage = useCallback(
    (language: Language) => currentLanguage === language,
    [currentLanguage]
  );

  /**
   * Get the direction (LTR/RTL) of the current language
   */
  const direction = currentConfig.direction;

  /**
   * Check if current language is RTL
   */
  const isRTL = direction === 'rtl';

  return {
    // Current language state
    currentLanguage,
    currentConfig,
    direction,
    isRTL,

    // Language switching functions
    changeLanguage,
    toggleLanguage,
    isLanguage,

    // Available languages
    supportedLanguages: Object.values(SUPPORTED_LANGUAGES),

    // i18n instance for advanced usage
    i18n,
  };
}
